import { useState, useEffect, useRef } from 'react';
import { X, Send, Loader2 } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/auth/hooks';
import toast from 'react-hot-toast';

interface TicketDetailsModalProps {
  ticketId: string;
  onClose: () => void;
  onUpdate: () => void;
}

interface Ticket {
  id: string;
  ticket_number: string;
  subject: string;
  category: string;
  priority: string;
  status: string;
  description: string;
  created_at: string;
}

interface Message {
  id: string;
  ticket_id: string;
  sender_id: string;
  sender_type: 'customer' | 'lf_admin';
  message: string;
  created_at: string;
}

const categoryIcons: Record<string, string> = {
  Technical: '🔧',
  Billing: '💰',
  'Feature Request': '✨',
  'Bug Report': '🐛',
  General: '💬',
};

const statusColors: Record<string, string> = {
  Open: 'bg-red-100 text-red-800',
  'In Progress': 'bg-blue-100 text-blue-800',
  'Waiting for Customer': 'bg-yellow-100 text-yellow-800',
  Resolved: 'bg-green-100 text-green-800',
  Closed: 'bg-slate-100 text-slate-800',
};

const priorityColors: Record<string, string> = {
  Urgent: 'bg-red-600 text-white',
  High: 'bg-orange-600 text-white',
  Medium: 'bg-yellow-600 text-white',
  Low: 'bg-slate-400 text-white',
};

export default function TicketDetailsModal({
  ticketId,
  onClose,
  onUpdate,
}: TicketDetailsModalProps) {
  const { user } = useAuth();
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadTicket();
    loadMessages();

    window.dispatchEvent(new CustomEvent('ui:support-modal', { detail: { open: true } }));

    const messagesChannel = supabase
      .channel(`ticket_messages:${ticketId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'ticket_messages',
          filter: `ticket_id=eq.${ticketId}`,
        },
        () => {
          loadMessages();
        }
      )
      .subscribe();

    return () => {
      messagesChannel.unsubscribe();
      window.dispatchEvent(new CustomEvent('ui:support-modal', { detail: { open: false } }));
    };
  }, [ticketId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  function scrollToBottom() {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }

  async function loadTicket() {
    try {
      const { data, error } = await supabase
        .from('support_tickets')
        .select('*')
        .eq('id', ticketId)
        .single();

      if (error) throw error;
      setTicket(data);
    } catch (error) {

      toast.error('שגיאה בטעינת הטיקט');
    }
  }

  async function loadMessages() {
    try {
      const { data, error } = await supabase
        .from('ticket_messages')
        .select('*')
        .eq('ticket_id', ticketId)
        .eq('is_internal_note', false)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setMessages(data || []);
    } catch (error) {

      toast.error('שגיאה בטעינת ההודעות');
    } finally {
      setLoading(false);
    }
  }

  async function handleSendMessage(e: React.FormEvent) {
    e.preventDefault();

    if (!newMessage.trim() || sending) return;

    try {
      setSending(true);

      const { error } = await supabase.from('ticket_messages').insert({
        ticket_id: ticketId,
        sender_id: user?.id,
        sender_type: 'customer',
        message: newMessage.trim(),
        is_internal_note: false,
      });

      if (error) throw error;

      setNewMessage('');
      onUpdate();
    } catch (error) {

      toast.error('שגיאה בשליחת ההודעה');
    } finally {
      setSending(false);
    }
  }

  const isClosed = ticket?.status === 'Closed';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl h-[90vh] flex flex-col">
        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : (
          <>
            <div className="border-b border-slate-200 px-6 py-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h2 className="text-2xl font-bold text-slate-900">
                      {ticket?.ticket_number}
                    </h2>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        statusColors[ticket?.status || '']
                      }`}
                    >
                      {ticket?.status}
                    </span>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        priorityColors[ticket?.priority || '']
                      }`}
                    >
                      {ticket?.priority}
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold text-slate-800 mb-1">{ticket?.subject}</h3>
                  <div className="flex items-center gap-4 text-sm text-slate-600">
                    <div className="flex items-center gap-1">
                      <span>{categoryIcons[ticket?.category || '']}</span>
                      <span>{ticket?.category}</span>
                    </div>
                    <span>•</span>
                    <span>נוצר ב-{new Date(ticket?.created_at || '').toLocaleString('he-IL')}</span>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-slate-600" />
                </button>
              </div>

              <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                <p className="text-sm font-medium text-slate-700 mb-1">תיאור הבעיה:</p>
                <p className="text-sm text-slate-600 whitespace-pre-wrap">{ticket?.description}</p>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {messages.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <p className="text-slate-600">עדיין אין הודעות בטיקט זה</p>
                    <p className="text-sm text-slate-500 mt-1">
                      צוות התמיכה יגיב בהקדם האפשרי
                    </p>
                  </div>
                </div>
              ) : (
                messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${
                      message.sender_type === 'customer' ? 'justify-end' : 'justify-start'
                    }`}
                  >
                    <div
                      className={`max-w-[70%] rounded-lg px-4 py-3 ${
                        message.sender_type === 'customer'
                          ? 'bg-blue-600 text-white'
                          : 'bg-slate-100 text-slate-900'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap">{message.message}</p>
                      <p
                        className={`text-xs mt-2 ${
                          message.sender_type === 'customer'
                            ? 'text-blue-100'
                            : 'text-slate-500'
                        }`}
                      >
                        {new Date(message.created_at).toLocaleString('he-IL', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            {!isClosed && (
              <div className="border-t border-slate-200 p-4">
                <form onSubmit={handleSendMessage} className="flex gap-3">
                  <textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="כתוב תגובה..."
                    rows={2}
                    className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    disabled={sending}
                  />
                  <button
                    type="submit"
                    disabled={!newMessage.trim() || sending}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {sending ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        <span>שלח</span>
                      </>
                    )}
                  </button>
                </form>
              </div>
            )}

            {isClosed && (
              <div className="border-t border-slate-200 p-4 bg-slate-50">
                <p className="text-center text-slate-600">טיקט זה נסגר ולא ניתן להוסיף הודעות</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
